import { Megaphone } from 'lucide-react';
import type { InboxChannel } from '../../types/inbox';
import { CHANNEL_LABEL } from '../../lib/inboxView';
import { cn } from '../../lib/utils';

// Per-channel brand glyphs. lucide dropped brand icons, so Facebook /
// Instagram / Google are tiny inline SVGs (metaads keeps the Megaphone "Ad"
// motif). Sized via className (default h-4 w-4).
//
// Shared primitive (promoted out of the community module 2026-07-19): the
// Community inbox and the Publishing calendar + account rail both render these
// glyphs, and `PostPlatform` is a subset of `InboxChannel` so publishing passes
// its platforms straight through.

function FacebookGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.52 1.5-3.91 3.78-3.91 1.09 0 2.23.2 2.23.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.57v1.88h2.78l-.45 2.9h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94Z" />
    </svg>
  );
}

function InstagramGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M21.6 12.23c0-.68-.06-1.36-.19-2.02H12v3.83h5.4a4.6 4.6 0 0 1-2 3.02v2.5h3.22c1.89-1.74 2.98-4.3 2.98-7.33Z" />
      <path d="M12 21.5c2.7 0 4.96-.89 6.62-2.42l-3.23-2.5c-.9.6-2.05.95-3.39.95-2.6 0-4.8-1.76-5.6-4.12H3.07v2.58A10 10 0 0 0 12 21.5Z" />
      <path d="M6.4 13.4a6 6 0 0 1 0-3.8V7.02H3.07a10 10 0 0 0 0 8.96L6.4 13.4Z" />
      <path d="M12 6.46c1.47 0 2.79.5 3.83 1.5l2.86-2.86A9.6 9.6 0 0 0 12 2.5a10 10 0 0 0-8.93 5.52L6.4 10.6c.8-2.37 3-4.13 5.6-4.13Z" />
    </svg>
  );
}

export function ChannelIcon({
  channel,
  className,
}: {
  channel: InboxChannel;
  className?: string;
}) {
  const cls = cn('h-4 w-4 shrink-0', className);
  const title = CHANNEL_LABEL[channel];
  switch (channel) {
    case 'facebook':
      return (
        <span title={title} className="inline-flex text-[#1877F2]">
          <FacebookGlyph className={cls} />
        </span>
      );
    case 'instagram':
      return (
        <span title={title} className="inline-flex text-[#E1306C]">
          <InstagramGlyph className={cls} />
        </span>
      );
    case 'googlebusiness':
      return (
        <span title={title} className="inline-flex text-slate-600">
          <GoogleGlyph className={cls} />
        </span>
      );
    case 'metaads':
      return (
        <span title={title} className="inline-flex text-purple-600">
          <Megaphone className={cls} />
        </span>
      );
  }
}
